import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowRight, Zap, Shield, BarChart3, Bot, Layers, Globe,
  Users,  Database, FileText, Cpu
} from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

const fade = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { animate: { transition: { staggerChildren: 0.08 } } };

export default function LandingPage() {
  const [config, setConfig] = useState({ mode: 'community' });
  
  const navigate = useNavigate();
  const isCommunity = config.mode === 'community';

  useEffect(() => {
    axios.get(`${API_BASE}/public/config`).then(r => { if (r.data) setConfig(r.data); }).catch(() => {});
    // Team removed from landing
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="pt-12 sm:pt-20 pb-12 sm:pb-16 px-4 sm:px-6">
        <motion.div variants={stagger} initial="initial" animate="animate"
          className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div>
            {isCommunity && (
              <motion.div variants={fade} className="mb-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                  <Globe className="w-3 h-3" /> Community Research Platform
                </span>
              </motion.div>
            )}
            <motion.h1 variants={fade}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-[-0.02em] leading-[1.1]"
              style={{ fontFamily: 'Space Grotesk' }}>
              {isCommunity
                ? <>Multi-Agent AI for<br /><span className="text-primary">Pharmacometrics</span></>
                : <>The AI Platform for<br /><span className="text-primary">Clinical Pharmacology</span></>}
            </motion.h1>
            <motion.p variants={fade} className="text-sm sm:text-base lg:text-lg text-muted-foreground mt-4 sm:mt-6 max-w-lg leading-relaxed">
              13 specialized AI agents orchestrating NCA, PopPK, PBPK, E-R analyses, simulations, and regulatory reporting — all through a single conversational interface with privacy-first architecture.
            </motion.p>
            <motion.div variants={fade} className="flex flex-wrap gap-3 mt-6 sm:mt-8">
              <Button onClick={() => navigate('/signup')} className="rounded-full h-10 sm:h-12 px-5 sm:px-6 text-sm active:scale-[0.98]">
                Try App <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/architecture')} className="rounded-full h-10 sm:h-12 px-5 sm:px-6 text-sm">
                View Architecture
              </Button>
              {isCommunity && (
                <Button variant="ghost" onClick={() => navigate('/join')} className="rounded-full h-10 px-4 text-sm text-muted-foreground">
                  Join Community
                </Button>
              )}
            </motion.div>
            <motion.div variants={fade} className="flex flex-wrap gap-4 sm:gap-6 mt-5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> 13 Agents</span>
              <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> 76 Templates</span>
              <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Privacy-First</span>
              <span className="flex items-center gap-1.5"><Database className="w-3.5 h-3.5" /> 151 Tools</span>
            </motion.div>
          </div>

          {/* Agent hierarchy visual */}
          <motion.div variants={fade} className="hidden lg:block">
            <div className="glass rounded-2xl p-5 space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full bg-destructive/60" />
                <div className="w-3 h-3 rounded-full bg-accent/60" />
                <div className="w-3 h-3 rounded-full bg-green-500/60" />
                <span className="ml-2 text-[10px] text-muted-foreground font-mono">pharmax.ai</span>
              </div>
              <div className="text-center mb-2">
                <span className="inline-block px-3 py-1.5 rounded-lg bg-primary/15 text-primary text-xs font-semibold">Supervisor</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {['Data', 'NCA', 'PopPK', 'QC', 'Report'].map(a => (
                  <div key={a} className="px-2 py-1.5 rounded bg-muted/30 text-center">
                    <span className="text-[9px] font-mono text-muted-foreground">{a}</span>
                  </div>
                ))}
              </div>
              <div className="widget-block p-2.5 mt-2">
                <p className="text-[9px] font-mono text-muted-foreground mb-1.5">NCA Summary</p>
                <div className="grid grid-cols-4 gap-1.5 text-[8px] font-mono">
                  <div className="text-muted-foreground">Param</div><div className="text-muted-foreground">GMean</div>
                  <div className="text-muted-foreground">CV%</div><div className="text-muted-foreground">N</div>
                  <div className="text-primary">Cmax</div><div>1.58</div><div>32%</div><div>245</div>
                  <div className="text-primary">AUC</div><div>18.5</div><div>28%</div><div>245</div>
                  <div className="text-primary">t½</div><div>7.2h</div><div>15%</div><div>230</div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats */}
      <section className="py-8 px-4 sm:px-6 border-y border-border/30">
        <div className="max-w-[1200px] mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          {[{ v: '13', l: 'AI Agents' }, { v: '76', l: 'Templates' }, { v: '24', l: 'Guidances' }, { v: '151', l: 'Tools' }].map(s => (
            <div key={s.l}>
              <p className="text-2xl sm:text-3xl font-bold font-mono text-primary">{s.v}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.l}</p>
            </div>
          ))}
        </div>
      </section>


      {/* Features */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8" style={{ fontFamily: 'Space Grotesk' }}>
            Platform Capabilities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Bot, title: 'Multi-Agent AI', desc: '13 specialized agents for NCA, PopPK, PBPK, E-R, simulations, QC, and reporting' },
              { icon: Shield, title: 'Privacy-First', desc: 'Raw patient data never reaches the LLM. Schema-only metadata approach.' },
              { icon: Layers, title: '76 Workflow Templates', desc: 'Structured multi-step analyses with human review gates and parameters' },
              { icon: BarChart3, title: 'Interactive Visualizations', desc: 'Spaghetti plots, VPC, forest plots, GOF gallery, E-R curves, heatmaps' },
              { icon: FileText, title: 'Regulatory Reports', desc: 'FDA/EMA-formatted DOCX with NCA tables, methods sections, labeling language' },
              { icon: Globe, title: 'Regulatory Intelligence', desc: 'RAG search over FDA/EMA/ICH guidances with cited responses' },
            ].map((f, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.06 }}>
                <Card className="card-hover h-full">
                  <CardContent className="pt-5 pb-4">
                    <f.icon className="w-7 h-7 text-primary mb-3" />
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>{f.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            {isCommunity ? 'Join the Community' : 'Start Analyzing'}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            {isCommunity ? 'Try Pharmax.ai and contribute to the future of AI-powered pharmacometrics.' : 'No credit card required.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <Button onClick={() => navigate('/signup')} className="rounded-full h-11 px-6 text-sm">
              Try App <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            {isCommunity && (
              <Button variant="outline" onClick={() => navigate('/join')} className="rounded-full h-11 px-6 text-sm">
                Request Architecture Docs
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
